import axios, { AxiosResponse } from "axios";
import React, { useEffect, useState, useContext, FC } from "react";
import { config } from "../../contexts/UrlContext";
import { toast } from "react-toastify";
import { Table, Icon, Popup, Dropdown, Menu } from 'semantic-ui-react';
import { SettingsContext } from "../../contexts/SettingsContext";
import { IGrid } from "../Interfaces/TableInterface";
import history from "../../History";
import IFrameSettingsModal from "./IFrameSettingsModal";
import EPathwayLinkModal from "./EPathwayLinkModal";
import DeleteTableModal from "./DeleteTableModal";
import AuthContext from "../../contexts/AuthContext";
import { getRequest } from "../../services/axiosService";
import { stringIsEmpty } from "../../utils/stringUtils";


interface IProps {
    id: string,
    table: IGrid,
    onUnlock: any,
    onDelete: any,
    setLoading: any,
    setLoadingText: any
}

interface IFrameSettings {
    height: string,
    width: string
}

const TableItem: FC<IProps> = ({ id, table, onUnlock, onDelete, setLoading, setLoadingText }) => {

    const { isAdmin, hasAdminPrivileges, hasBasicPrivileges, currentUser } = useContext(SettingsContext);
    const { auth } = useContext(AuthContext);

    const archiveId = `${id}-archiveBtn`;

    const [openIFrame, setOpenIFrame] = useState<boolean>(false);
    const [openPathwayLink, setOpenPathwayLink] = useState<boolean>(false);
    const [openDeleteTable, setOpenDeleteTable] = useState<boolean>(false);
    const [disableCache, setDisableCache] = useState<boolean>(false);

    useEffect(() => {
      
    }, []);


    const unlockTable = async () => {
        setLoading(true);
        try {
            let response: AxiosResponse;

            if (!auth || auth === null) {
                response = await axios.post(`${config.baseUrl}/api/Home/UnlockTable`,
                    null, {
                    params: {
                        id,
                    }
                });
            } else {
                let header = {
                    Authorization: `Bearer ${auth.user?.access_token}`,
                }
                response = await axios.post(`${config.baseUrl}/api/Home/UnlockTable`,
                    null, {
                    params: {
                        id,
                    }, 
                    headers: header
                });
            }

            onUnlock(id);
        } catch (error) {
            console.log(error);
            toast.error(`Unable to unlock table. Please check logs`);
        }
        setLoading(false);
    }

    const deleteTable = async (canDelete: boolean) => {
        if (!canDelete){
            setOpenDeleteTable(false);
            return;
        }

        try {
            let response: AxiosResponse;

            if (!auth || auth === null) {
                response = await axios.post(`${config.baseUrl}/api/Home/DeleteTable`,
                    null, {
                    params: {
                        id,
                    }
                });
            } else {
                let header = {
                    Authorization: `Bearer ${auth.user?.access_token}`,
                }
                response = await axios.post(`${config.baseUrl}/api/Home/DeleteTable`,
                    null, {
                    params: {
                        id,
                    }, 
                    headers: header
                });
            }

            onDelete(id);
        } catch (error) {
            console.log(error);
            toast.error(`Unable to delete table. Please check logs`);
        }
    }

    const copyURLLink = () => {
        let link = `${config.baseUrl}/Grid?id=` + id;
        navigator.clipboard.writeText(link);
        toast.success("URL link has been copied to clipboard");
    }

    const copyiFrameLink = (iFrameSettings: IFrameSettings) => {
        setOpenIFrame(false);

        let height = iFrameSettings.height === undefined ? "100%" : iFrameSettings.height + "px";
        let width = iFrameSettings.width === undefined ? "100%" : iFrameSettings.width + "px";

        let link = `<iframe src="${config.baseUrl}/Grid?id=${id}" frameborder="0" style="position:relative" height="${height}" width="${width}" allowfullscreen></iframe>`;
        navigator.clipboard.writeText(link);

        toast.success("iFrame link has been copied to clipboard");
    }

    const copyePathwayLink = async (linkType: string) => {
        setOpenPathwayLink(false);
        try {
            setLoadingText("Generating EPathway Link");
            setLoading(true);

            let response: AxiosResponse = await getRequest(auth, `/Home/GetEPathwayLink?id=${id}&linkType=${linkType}`);

            let link = "";
            if (linkType.toLocaleLowerCase() === "dynamiclink") {
                link = `${config.baseUrl}/Grid?` + response.data;
            }
            else {
                link = `${config.baseUrl}/Grid?query=` + response.data;
            }
            navigator.clipboard.writeText(link);
            setLoading(false);
            toast.success("URL link has been copied to clipboard");
        } catch (e) {
            console.log(e);
            setLoading(false);
            toast.error(`Unable to copy table index. Please check logs`);
        }
    }

    const copyTableIndex = async () => {
        try
        {
            setLoadingText("Generating CSV Table Index");
            setLoading(true);

            let response: AxiosResponse;

            if (!auth || auth === null) {
                response = await axios.get(`${config.baseUrl}/api/ContentManager/GetTableIndex?id=${id}`);
            } else {
                let header = {
                    Authorization: `Bearer ${auth.user?.access_token}`,
                }
                response = await axios.get(`${config.baseUrl}/api/ContentManager/GetTableIndex?id=${id}`, { headers: header });
            }

            let link  = response.data;
            navigator.clipboard.writeText(link);
            setLoading(false);
            toast.success("Table Index has been copied to clipboard");

        } catch (e) {
            console.log(e);
            setLoading(false);
            toast.error(`Unable to copy table index. Please check logs`);
        }
    }

    const onCacheClick = async () => {
        if (disableCache) {
            toast.warning("Please wait for the current cache to complete");
            return;
        }

        let cacheBtn: HTMLElement = document.getElementById(archiveId)!;
        cacheBtn.classList.add("loadingIcon");
        cacheBtn.classList.add("disabled");
        setDisableCache(true);

        try {
            let response: AxiosResponse = await getRequest(auth, `/Grid/CacheTable?id=${id}`);
            if (response) {
                toast.success(`${table.name} Table successfully cached`);
            }
            

        } catch (e) {
            console.log(e);
            toast.error("Unable to cache table, see logs for more info");
        }
        cacheBtn.classList.remove("loadingIcon");
        cacheBtn.classList.remove("disabled");
        setDisableCache(false);

    }

    const downloadCSV = async () => {
        //Determine mime type from extension 
        setLoadingText("Generating CSV file");
        setLoading(true);

        try {
            let response: AxiosResponse = await getRequest(auth, `/ContentManager/DownloadCSV?id=${id}`);

            let files = response.data;

            let linkSource = `data:${files.mimeType};base64,${files.contents}`;
            let downloadLink = document.createElement('a');
            document.body.appendChild(downloadLink);

            downloadLink.href = linkSource;
            downloadLink.target = '_blank';
            downloadLink.download = files.recordSuggestedFileName;
            downloadLink.click();
            toast.success("CSV file successfully downloaded");
        } catch (e) {
            console.log(e);
            toast.error(`Unable to download CSV. Please see logs for more info`);
        }
        setLoading(false);
    }

    const openNewTab = () => {
        if (table?.id) {
            let baseUrl = config.baseUrl.toLowerCase().includes("cm2webshare") 
                ? config.baseUrl 
                : `${config.baseUrl}/CM2WebShare`;
            window.open(`${baseUrl}/Grid?id=${table.id}`, '_blank');
        } else {
            toast.error("Unable to open new tab. Table ID is missing.");
        }
    }

    return (
        <>
            <Table.Row key={table.id}>
                <Table.Cell>
                    {table.shortName !== null && (
                        <Popup size="small" position="top center" wide trigger={
                            <div>
                                {table.shortName}
                            </div>
                        }>{table.name}</Popup>
                    )}

                    {table.shortName === null && (
                        <div>
                            {table.name}
                        </div>
                    )}
                </Table.Cell>
                <Table.Cell>
                    {!stringIsEmpty(table.shortDescription) && (
                        <Popup size="small" position="top center" wide trigger={
                            <div>
                                {table.shortDescription}
                            </div>
                        }>{table.description}</Popup>
                    )}

                    {stringIsEmpty(table.shortDescription) && (
                        <div>
                            {table.description}
                        </div>
                    )}
                </Table.Cell>
                <Table.Cell>
                    {table.lastUpdatedBy}
                </Table.Cell>
                <Table.Cell>
                    {table.lastUpdatedDate}
                </Table.Cell>
                <Table.Cell>
                   
                    {/*Unlocking of a table that is not locked by you is reserved for CM Admins only*/}

                    {isAdmin && table.checkedOutTo !== null && table.checkedOutTo !== currentUser && (
                        <a><Popup content={`Table is currently locked to ${table.checkedOutTo}. Unlock this table`} trigger={<Icon name="lock" alt='Table Locked' id='locked' style={{ cursor: 'pointer' }} onClick={unlockTable} />} /></a>
                    )}

                    {!isAdmin && (hasAdminPrivileges && table.checkedOutTo !== null && table.checkedOutTo !== currentUser) && (
                        <a><Popup content="Table is currently locked." trigger={<Icon name="lock" alt='Table Locked' id='locked'  style={{ cursor: 'pointer' }} />} /></a>
                    )}

                    {hasAdminPrivileges && table.checkedOutTo === currentUser && (
                        <>
                            <a><Popup content="Unlock this table" trigger={<Icon name="unlock alternate" alt='Unlock Table' id='unlock' style={{ cursor: 'pointer' }} onClick={unlockTable} />} /></a>
                            <a><Popup content="Edit table" trigger={<Icon name="pencil alternate" alt='Edit Table' id='edit'style={{ cursor: 'pointer' }} onClick={() => history.push({ pathname: '/CM2WebShare/GridSettings', search: `?id=${table.id}` })} />} /></a>
                        </>
                    )}

                    {hasAdminPrivileges && table.checkedOutTo === null && (
                        <a><Popup content="Edit table" trigger={<Icon name="pencil alternate" alt='Edit Table' id='edit'  style={{ cursor: 'pointer' }} onClick={() => history.push({ pathname: '/CM2WebShare/GridSettings', search: `?id=${table.id}` })} />} /></a>
                    )}

                    {hasAdminPrivileges && (
                        <a><Popup content="Delete table" trigger={<Icon name="trash alternate outline" alt='Delete Table' id='delete' style={{ cursor: 'pointer' }} onClick={() => { setOpenDeleteTable(true) }} />} /></a>
                    )}

                    <a><Popup content="View table" trigger={<Icon name="eye" alt='View Table' id='view' style={{ cursor: 'pointer' }} onClick={openNewTab} />} /></a>

                    {hasAdminPrivileges && table.cacheTableData && (
                        <a><Popup basic content={stringIsEmpty(table.lastCachedDate) ? `Cache Table Results`: `Table last cached at ${table.lastCachedDate}`} trigger={<Icon
                            style={{ height: "100%", width: "auto", cursor: "pointer" }}
                            name='sync'
                            className={`home-icon ${disableCache ? "loadingIcon disabled" : ""}`}
                            id={archiveId}
                            disabled={disableCache}
                            onClick={onCacheClick}
                        />} /></a>
                    )}
                    
                    <Dropdown item icon='ellipsis vertical' simple>
                        <Dropdown.Menu>
                            <Dropdown.Header>Share Options</Dropdown.Header>
                            <Dropdown.Item onClick={copyURLLink}>Copy URL link</Dropdown.Item>
                            <Dropdown.Item onClick={() => setOpenIFrame(true)}>Copy iFrame link</Dropdown.Item>
                            <Dropdown.Item onClick={() => { setOpenPathwayLink(true) }}>Copy ePathway link</Dropdown.Item>
                            {hasAdminPrivileges && (
                                <>
                                    <Dropdown.Item onClick={copyTableIndex}>Copy table index</Dropdown.Item>
                                    <Dropdown.Item onClick={downloadCSV}>Download CSV</Dropdown.Item>
                                </>
                            )}
                        </Dropdown.Menu>
                    </Dropdown>
                </Table.Cell>
            </Table.Row>
            <IFrameSettingsModal open={openIFrame} onConfirm={copyiFrameLink} onClose={() => { setOpenIFrame(false) }} />
            <EPathwayLinkModal open={openPathwayLink} onChange={copyePathwayLink} onClose={() => { setOpenPathwayLink(false) }} />
            <DeleteTableModal open={openDeleteTable} onChange={deleteTable} onClose={() => { setOpenDeleteTable(false) }} />
        </>
    )                                         
}

export default TableItem;